<script setup lang="ts">
import { VModal, VBadge, VTextField, VSection, VButton } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { Invoice } from '../types'
import { format } from '@/modules/shared/utils/v-table'
import { useExtendedI18n } from '@/i18n'
import { toNumber } from '@/modules/shared/utils/money'
import { computed, ref } from 'vue'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { useInvoiceStore } from '../stores/invoice-store'

const props = defineProps<{
  invoiceIds: string[] // this is an array to support marking multiple invoices as paid
  callback?: any
}>()

const isLoading = ref(false)
const modal = useModal()
const store = useInvoiceStore()

const callback = async () => {
  if (props.callback) await props.callback()
}

defineExpose({
  modal,
})

const initialState = {
  invoice_ids: [],
  paid_at: null,
}
const rules = {
  invoice_ids: { required },
  paid_at: { required },
}
const form = ref({ ...initialState })
const v$ = useVuelidate(rules, form, { $lazy: true })

const submit = async () => {
  form.value.invoice_ids = [...props.invoiceIds]
  const valid = await v$.value.$validate()
  if (!valid) return

  isLoading.value = true
  await store.markMultipleInvoicesAsPaid(form.value)
  await callback()
  isLoading.value = false
  modal.close()

  // reset
  form.value = { ...initialState }
}

const mark_single_invoice = computed(() => props.invoiceIds.length === 1)
</script>

<template>
  <VModal :modalStore="modal">
    <template #main>
      <VSection :label="mark_single_invoice ? 'Mark invoice as paid' : 'Mark multiple invoices as paid'">
        <slot name="actions"></slot>
        <form @submit.prevent="submit">
          <div class="mt-6 space-y-3">
            <VTextField
              v-model="form.paid_at"
              label="Payment date"
              property="paid_at"
              ref="autofocus"
              type="date"
              :v$="v$"
            />
          </div>
        </form>
      </VSection>
    </template>
    <template #footer>
      <div class="flex items-center justify-between space-x-3">
        <VButton :click="modal.close" size="lg">Close</VButton>
        <VButton :click="submit" class="w-full" size="lg" :loading="isLoading" variant="primary"> Submit </VButton>
      </div>
    </template>
  </VModal>
</template>
